// User Types
export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  USER = 'user'
}

export enum EntityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PROCESSING = 'processing'
}

export interface User {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
}

export interface UserCreate {
  username: string;
  password: string;
  email?: string;
  role: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  email?: string;
  role: 'admin' | 'manager' | 'user';
}

// Entity Types
export interface Entity {
  id: string;
  entity_id: string; // Primary identifier from backend
  name: string;
  description?: string;
  data?: string; // Knowledge base content/data
  processed_data?: string; // AI-processed data
  processing_status?: string | null; // "pending", "processing", "completed", "failed", null
  last_processed?: string; // Last processing timestamp
  created_at: string;
  updated_at: string;
  status?: EntityStatus; // Optional for backward compatibility
  file_count?: number; // Optional for backward compatibility
  is_processed?: boolean; // Optional for backward compatibility
  url_count?: number; // Optional for backward compatibility
  user_id?: string; // Optional for backward compatibility
}

export interface EntityCreate {
  name: string;
  description?: string;
  data: string;
}

export interface EntityUpdate {
  name?: string;
  description?: string;
  data?: string;
}

// Chat Types
export interface ChatRequest {
  message: string;
  entity_id: string;
}

export interface ChatResponse {
  response: string;
  entity_id: string;
  entity_name: string;
  confidence_score: number;
}

// Snippet Types
export interface SnippetRequest {
  entity_id: string;
}

export interface SnippetResponse {
  entity_id: string;
  snippet_html: string;
  snippet_js: string;
  api_key: string;
  created_at: string;
}

// System Types
export interface SystemStats {
  total_users: number;
  active_users: number;
  total_entities: number;
  active_entities: number;
  total_files: number;
  total_urls: number;
  system_health: string;
  last_updated: string;
}

export interface SystemHealth {
  status: string;
  uptime: string;
  response_time: string;
  error_rate: string;
  active_connections: number;
  memory_usage: string;
  cpu_usage: string;
  disk_usage: string;
  last_check: string;
}

// API Response Types
export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  status: string;
  message: string;
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

// Auth Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  user_id: string;
  username: string;
  role: string;
}

export interface RegisterResponse {
  status: string;
  message: string;
  data: {
    id: string;
    username: string;
    email?: string;
    role: string;
    is_active: boolean;
  };
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// State Types
export interface UserState {
  users: User[];
  isLoading: boolean;
  error: string | null;
}

export interface EntityState {
  entities: Entity[];
  isLoading: boolean;
  error: string | null;
}

export interface SystemState {
  stats: SystemStats | null;
  health: SystemHealth | null;
  isLoading: boolean;
  error: string | null;
}

// UI Types
export interface SidebarItem {
  title: string;
  path: string;
  icon: React.ComponentType;
  children?: SidebarItem[];
  allowedRoles?: UserRole[];
}

export interface TableColumn {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  renderCell?: (params: any) => React.ReactNode;
}

export interface ChartData {
  name: string;
  value: number;
  color?: string;
}

// Data Processing Types
export interface ProcessDataRequest {
  entityId: string; // Required: entity ID to process
  data: string; // Required: text data to process
}

export interface ProcessDataResponse {
  status: string;
  message: string;
  data: {
    processed_entities: number;
    processed_files: number;
    processed_urls: number;
    processing_time: number;
    errors: string[];
    warnings: string[];
  };
}

export interface ProcessingStatus {
  is_processing: boolean;
  current_entity?: string;
  progress: number;
  total_entities: number;
  processed_entities: number;
  estimated_time_remaining?: number;
  started_at?: string;
}

export interface ProcessingHistoryItem {
  id: string;
  started_at: string;
  completed_at?: string;
  status: 'completed' | 'failed' | 'in_progress';
  processed_entities: number;
  processed_files: number;
  processed_urls: number;
  processing_time: number;
  errors: string[];
  warnings: string[];
  triggered_by: string;
}

// Toast Types
export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: number;
}

export interface ToastState {
  toasts: Toast[];
}
